import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Deep-link query param naming the target turn, as emitted in feedback Slack links.
export const TURN_PARAM = "turnId";

// Turn ids are stored as the full X-Ray header (`Root=1-...;Parent=...;Sampled=1`);
// pull out just the root trace id we use as the DOM anchor, mirroring the
// backend's `parse_trace_id`.
const TRACE_ROOT_PATTERN = /1-[0-9a-f]{8}-[0-9a-f]{24}/i;

// Reduce a raw turn id to its root trace id, falling back to the raw value.
export function turnAnchorId(rawTurnId: string | undefined): string | undefined {
  if (!rawTurnId) {
    return undefined;
  }
  return TRACE_ROOT_PATTERN.exec(rawTurnId)?.[0] ?? rawTurnId;
}

// "Near the bottom" threshold (px), shared by follow-new-content and Chat's
// jump-to-bottom button so the two never disagree.
export const BOTTOM_THRESHOLD = 150;

// How long to keep re-pinning an anchor as late content reflows in before
// letting the user take over.
const SETTLE_MS = 1500;

// The target turn can mount after the effect runs (async history, conditional
// container), so poll for it.
const FIND_INTERVAL_MS = 100;
const MAX_FIND_ATTEMPTS = 20;

// Re-pin a turn only past this much drift, so sub-pixel noise doesn't stutter
// the smooth scroll. (The bottom edge always re-pins — snapping to it is idempotent.)
const DRIFT_TOLERANCE_PX = 4;

// How long the landed turn stays highlighted.
const HIGHLIGHT_MS = 2000;

// Hold a scroll anchor through late reflow: run `onReflow` on each content
// resize until the user scrolls or the settle window elapses. The caller does
// the opening scroll, so the initial ResizeObserver callback is skipped —
// re-issuing before it moves would stutter a smooth scroll. Returns an
// idempotent `release`; `onRelease` fires once when the hold ends.
function holdThroughReflow(
  scrollEl: HTMLElement,
  content: Element,
  onReflow: () => void,
  onRelease?: () => void
): () => void {
  let released = false;
  let sawInitialObserve = false;

  const observer = new ResizeObserver(() => {
    if (released) {
      return;
    }
    if (!sawInitialObserve) {
      sawInitialObserve = true;
      return;
    }
    onReflow();
  });
  observer.observe(content);

  const release = () => {
    if (released) {
      return;
    }
    released = true;
    observer.disconnect();
    window.clearTimeout(capTimer);
    scrollEl.removeEventListener("wheel", release);
    scrollEl.removeEventListener("touchmove", release);
    onRelease?.();
  };
  // A deliberate scroll by the user takes over; stop pinning and clean up.
  scrollEl.addEventListener("wheel", release, { passive: true });
  scrollEl.addEventListener("touchmove", release, { passive: true });
  const capTimer = window.setTimeout(release, SETTLE_MS);

  return release;
}

// Pin to the bottom and keep it there through late reflow (artifacts, markdown).
function holdBottom(scrollEl: HTMLElement): () => void {
  const content = scrollEl.firstElementChild ?? scrollEl;
  const pinBottom = () => {
    scrollEl.scrollTop = scrollEl.scrollHeight;
  };
  pinBottom();
  return holdThroughReflow(scrollEl, content, pinBottom);
}

export interface AnchoredScroll {
  // Turn id a deep-link scroll just landed on, for `ConversationTurns` to flash;
  // null when nothing is highlighted.
  highlightedTurnId: string | null;
  // End any active hold. Call when the user deliberately takes over (jump to
  // bottom, submit, answer) — those don't fire the wheel/touch events the hold
  // otherwise waits for, so without this the hold would re-pin over them.
  releaseHold: () => void;
}

// Owns a conversation view's scroll position. One anchor at a time, acquired
// then held through reflow:
//
//   - `?turnId=` deep link → scroll the turn to the top, highlight it, and
//     re-pin through reflow; fall back to a normal bottom open if it never
//     shows. Wins on open, and the param stays in the URL as a permalink.
//   - otherwise, if `followKey` is given → pin to the bottom on open and follow
//     new content (keyed on `followKey` changing) while the user stays near the
//     bottom. Omit `followKey` for read-only views, which open at the top.
export function useAnchoredScroll(
  scrollRef: RefObject<HTMLElement | null>,
  followKey?: unknown
): AnchoredScroll {
  const [searchParams] = useSearchParams();
  // Normalize so a clean `?turnId=1-...` and a legacy full-header link resolve
  // to the same anchor the turns render.
  const targetTurnId = turnAnchorId(searchParams.get(TURN_PARAM) ?? undefined) ?? null;
  const followEnabled = followKey !== undefined;
  const [highlightedTurnId, setHighlightedTurnId] = useState<string | null>(null);

  // True while a deep link is in control — from the moment it starts searching
  // for its turn through the hold that follows — so the follow effect can't jump
  // to the bottom underneath it mid-search. Cleared when the hold releases or a
  // stale link falls back, so follow then resumes.
  const deepLinkActiveRef = useRef(false);

  // Teardown of whichever hold is currently active, exposed via `releaseHold`.
  const releaseRef = useRef<(() => void) | null>(null);
  const releaseHold = useCallback(() => {
    releaseRef.current?.();
  }, []);

  // Pin to the bottom on open. Layout effect so we land there before paint (no
  // flash of the top first). Skipped when a deep link owns the open or the view
  // doesn't follow (read-only opens at the top).
  useLayoutEffect(() => {
    if (targetTurnId || !followEnabled) {
      return;
    }
    const scrollEl = scrollRef.current;
    if (!scrollEl) {
      return;
    }
    return holdBottom(scrollEl);
  }, [targetTurnId, followEnabled, scrollRef]);

  // Deep-link scroll: find the turn (it can mount late), land and hold it at the
  // top, or fall back to a normal bottom open on a stale link.
  useEffect(() => {
    if (!targetTurnId) {
      return;
    }

    // Take control up front so follow can't steal the scroll while we search.
    deepLinkActiveRef.current = true;
    let cancelled = false;

    const land = (scrollEl: HTMLElement, target: HTMLElement) => {
      const content = scrollEl.firstElementChild ?? scrollEl;
      const scrollToTop = () => target.scrollIntoView({ behavior: "smooth", block: "start" });

      scrollToTop();
      setHighlightedTurnId(targetTurnId);

      releaseRef.current = holdThroughReflow(
        scrollEl,
        content,
        () => {
          // Re-issue only once the turn has drifted; smooth re-issues coalesce
          // into one continuous animation.
          const drift = target.getBoundingClientRect().top - scrollEl.getBoundingClientRect().top;
          if (Math.abs(drift) > DRIFT_TOLERANCE_PX) {
            scrollToTop();
          }
        },
        () => {
          deepLinkActiveRef.current = false;
          releaseRef.current = null;
        }
      );
    };

    const find = (attempt: number) => {
      if (cancelled) {
        return;
      }
      const scrollEl = scrollRef.current;
      const target =
        scrollEl?.querySelector<HTMLElement>(`[data-turn-id="${CSS.escape(targetTurnId)}"]`) ??
        null;
      if (scrollEl && target) {
        land(scrollEl, target);
      } else if (attempt < MAX_FIND_ATTEMPTS) {
        window.setTimeout(() => find(attempt + 1), FIND_INTERVAL_MS);
      } else {
        // Gave up: a stale/cross-conversation link, or a container that never
        // mounted (empty chat). Either way, release control so follow resumes.
        // If the container is here, fall back to a normal bottom open with the
        // reflow hold, rather than a one-shot scroll that late artifacts could
        // push off the bottom.
        if (scrollEl) {
          releaseRef.current = holdBottom(scrollEl);
        }
        deepLinkActiveRef.current = false;
      }
    };
    find(0);

    return () => {
      cancelled = true;
      releaseRef.current?.();
      releaseRef.current = null;
      deepLinkActiveRef.current = false;
    };
  }, [targetTurnId, scrollRef]);

  // Follow new content while the user is near the bottom, unless a deep link is
  // in control (searching or holding). Off for read-only views.
  useEffect(() => {
    if (!followEnabled || deepLinkActiveRef.current) {
      return;
    }
    const scrollEl = scrollRef.current;
    if (!scrollEl) {
      return;
    }
    const distance = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
    if (distance < BOTTOM_THRESHOLD) {
      scrollEl.scrollTo({ top: scrollEl.scrollHeight });
    }
  }, [followKey, followEnabled, scrollRef]);

  // Auto-clear the highlight. Separate from the scroll effect so clearing the
  // URL param (which re-runs that effect) can't cancel this timer.
  useEffect(() => {
    if (!highlightedTurnId) {
      return;
    }
    const timer = window.setTimeout(() => setHighlightedTurnId(null), HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [highlightedTurnId]);

  return { highlightedTurnId, releaseHold };
}
